import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LANGUAGE_METADATA,
  getSwitcherLanguageCodes,
  normalizeLanguageCode,
} from '../../i18n/languages';
import styles from './LanguageSwitcher.module.scss';

function classNames(...values) {
  return values.filter(Boolean).join(' ');
}

function GlobeIcon({ className = '' }) {
  return (
    <span className={className} aria-hidden="true">
      <svg viewBox="0 0 20 20" focusable="false" role="presentation">
        <circle cx="10" cy="10" r="7" />
        <path d="M3 10h14" />
        <path d="M10 3a11 11 0 0 1 0 14" />
        <path d="M10 3a11 11 0 0 0 0 14" />
      </svg>
    </span>
  );
}

export default function LanguageSwitcher({ className = '', triggerStyle = 'flag' }) {
  const { i18n } = useTranslation();
  const menuId = useId();
  const buttonId = useId();

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);

  const options = useMemo(
    () =>
      getSwitcherLanguageCodes()
        .map((code) => LANGUAGE_METADATA[code])
        .filter(Boolean),
    [],
  );

  const selectedCode = normalizeLanguageCode(i18n.resolvedLanguage || i18n.language);
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.code === selectedCode),
  );
  const selectedLanguage = options[selectedIndex] || LANGUAGE_METADATA.en;
  const showGlobeTrigger = triggerStyle === 'globe';
  const showOptionFlags = !showGlobeTrigger;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      setOpen(false);
      triggerRef.current?.focus();
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setActiveIndex(selectedIndex);
    requestAnimationFrame(() => {
      itemRefs.current[selectedIndex]?.focus();
    });
  }, [open, selectedIndex]);

  const closeMenu = () => {
    setOpen(false);
  };

  const selectLanguage = (code) => {
    i18n.changeLanguage(code);
    closeMenu();
    triggerRef.current?.focus();
  };

  const moveActive = (nextIndex) => {
    const size = options.length;
    if (!size) {
      return;
    }

    const bounded = (nextIndex + size) % size;
    setActiveIndex(bounded);
    itemRefs.current[bounded]?.focus();
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen((current) => !current);
    }
  };

  const handleMenuKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveActive(activeIndex + 1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveActive(activeIndex - 1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      moveActive(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      moveActive(options.length - 1);
      return;
    }

    if (event.key === 'Tab') {
      closeMenu();
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const next = options[activeIndex];
      if (next?.code) {
        selectLanguage(next.code);
      }
    }
  };

  return (
    <div className={classNames(styles.root, className)} ref={rootRef}>
      <button
        id={buttonId}
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={handleTriggerKeyDown}
        ref={triggerRef}
      >
        {showGlobeTrigger ? (
          <GlobeIcon className={styles.globe} />
        ) : (
          <span aria-hidden="true" className={styles.flag}>
            {selectedLanguage.flag}
          </span>
        )}
        <span className={styles.code}>{selectedLanguage.shortCode}</span>
      </button>

      <div
        id={menuId}
        className={classNames(styles.menu, open ? styles.menuOpen : '')}
        role="menu"
        aria-labelledby={buttonId}
        onKeyDown={handleMenuKeyDown}
      >
        {options.map((option, index) => {
          const selected = option.code === selectedCode;
          return (
            <button
              key={option.code}
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              type="button"
              role="menuitemradio"
              aria-checked={selected}
              tabIndex={open && index === activeIndex ? 0 : -1}
              className={classNames(
                styles.option,
                !showOptionFlags ? styles.optionNoFlag : '',
                selected ? styles.optionSelected : '',
              )}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => selectLanguage(option.code)}
            >
              {showOptionFlags ? (
                <span aria-hidden="true" className={styles.flag}>
                  {option.flag}
                </span>
              ) : null}
              <span className={styles.label}>{option.nativeName}</span>
              <span className={styles.optionCode}>{option.shortCode}</span>
              <span className={classNames(styles.check, selected ? styles.checkVisible : '')} aria-hidden="true">
                ✓
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
